import { supabase } from '../lib/supabase.js';
import { getState, setState } from '../state/store.js';
import { isOnline, OFFLINE_SAVE_MESSAGE } from '../lib/onlineGuard.js';

// Loads EVERY transaction for the site (not just "recent") -- P&L needs the
// complete set to total correctly, and the Home screen just displays the first
// slice of the same array. This is the one data source both screens read from.
export async function loadRecentTransactions(siteId) {
  if (!siteId) {
    setState({ recentTransactions: [] });
    return;
  }
  setState({ recentTransactionsLoading: true });
  const { data, error } = await supabase
    .from('transactions')
    .select('id, entry_date, type, category, description, amount, payment_mode, notes, created_at')
    .eq('site_id', siteId)
    .order('entry_date', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Failed to load transactions', error);
    setState({ recentTransactions: [], recentTransactionsLoading: false });
    return;
  }
  setState({ recentTransactions: data || [], recentTransactionsLoading: false });
}

async function currentUserId() {
  const { session } = getState();
  if (session?.user?.id) return session.user.id;
  const { data } = await supabase.auth.getSession();
  return data.session?.user?.id || null;
}

/**
 * fields: { site_id, entry_date, category, description, amount, payment_mode, notes }
 * Returns { ok, error } -- caller must check `ok` before treating the save as successful.
 */
export async function saveExpense(fields) {
  if (!isOnline()) return { ok: false, error: OFFLINE_SAVE_MESSAGE };
  const userId = await currentUserId();
  if (!userId) return { ok: false, error: 'Not signed in' };
  if (!fields.site_id) return { ok: false, error: 'No site selected' };
  const amount = Number(fields.amount);
  if (!amount || amount <= 0) return { ok: false, error: 'Enter a valid amount' };
  if (!fields.category) return { ok: false, error: 'Category is required' };

  const { error } = await supabase.from('transactions').insert({
    site_id: fields.site_id,
    entry_date: fields.entry_date,
    type: 'expense',
    category: fields.category,
    description: fields.description || null,
    amount,
    payment_mode: fields.payment_mode || null,
    notes: fields.notes || null,
    created_by: userId
  });

  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

/**
 * fields: { site_id, entry_date, description, amount, payment_mode, notes }
 */
export async function saveIncome(fields) {
  if (!isOnline()) return { ok: false, error: OFFLINE_SAVE_MESSAGE };
  const userId = await currentUserId();
  if (!userId) return { ok: false, error: 'Not signed in' };
  if (!fields.site_id) return { ok: false, error: 'No site selected' };
  const amount = Number(fields.amount);
  if (!amount || amount <= 0) return { ok: false, error: 'Enter a valid amount' };

  const { error } = await supabase.from('transactions').insert({
    site_id: fields.site_id,
    entry_date: fields.entry_date,
    type: 'income',
    category: 'Income',
    description: fields.description || null,
    amount,
    payment_mode: fields.payment_mode || null,
    notes: fields.notes || null,
    created_by: userId
  });

  if (error) return { ok: false, error: error.message };
  return { ok: true };
}
